import { useEffect } from "react";

import classnames from "classnames";
import useQueryParams from "neetocommons/react-utils/useQueryParams";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";
import { useTranslation } from "react-i18next";

import Category from "./Category";
import { filterCategories, scrollIntoSettingCategory } from "./utils";

import Header from "../Header";

const Settings = ({
  isTitleHidden = false,
  isSearchHidden = false,
  title,
  headerProps = {},
  categories = [],
  className = "",
}) => {
  const { t } = useTranslation();

  const { category: categoryParam, searchTerm = "" } = useQueryParams();

  const filteredCategories = filterCategories({ categories, searchTerm });

  const isHeaderVisible = !isSearchHidden || !isTitleHidden;

  const headerTitle = title ?? t("neetoMolecules.settingsPage.title");

  useEffect(() => {
    scrollIntoSettingCategory(categoryParam);
  }, [categoryParam]);

  return (
    <div
      className={classnames(
        "flex h-dvh grow flex-col items-start justify-start overflow-y-auto px-5 md:px-10",
        { "h-auto overflow-y-auto": isTitleHidden },
        className
      )}
    >
      {isHeaderVisible && (
        <Header
          className={classnames({ "min-h-0 pt-8 pb-4": isTitleHidden })}
          title={!isTitleHidden && headerTitle}
          searchProps={
            isSearchHidden
              ? null
              : {
                  className: "w-72",
                  placeholder: t(
                    "neetoMolecules.settingsPage.placeholder.search"
                  ),
                }
          }
          {...headerProps}
        />
      )}
      {isEmpty(filteredCategories) ? (
        <div
          className="flex h-full w-full flex-grow items-center justify-center"
          data-testid="settings-no-data"
        >
          <div className="text-muted-foreground flex flex-col items-center gap-2">
            <p className="text-lg font-medium">
              {t("neetoMolecules.settingsPage.noData")}
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full">
          {filteredCategories.map(({ id, label, items, isIntegration }) => (
            <Category {...{ id, isIntegration, items, label }} key={id} />
          ))}
        </div>
      )}
    </div>
  );
};

Settings.propTypes = {
  /**
   * Whether to hide the title or not.
   */
  isTitleHidden: PropTypes.bool,
  /**
   * This can be used to customize the title.
   */
  title: PropTypes.string,
  /**
   * Whether to hide the search bar or not.
   */
  isSearchHidden: PropTypes.bool,
  /**
   * An array of setting categories to be displayed.
   */
  categories: PropTypes.array,
  /**
   * Additional classes to be added to the container.
   */
  className: PropTypes.string,
  /**
   * Props to be passed to the header component.
   */
  headerProps: PropTypes.object,
};

export default Settings;
